import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { UserPlus } from 'lucide-react'
import { SEOHead } from '@/components/SEOHead'
import { LocalizedLink } from '@/components/LocalizedLink'

interface FormData {
  name: string
  surname: string
  email: string
  password: string
  confirmPassword: string
  privacy: boolean
}

interface FormErrors {
  name?: string
  surname?: string
  email?: string
  password?: string
  confirmPassword?: string
  privacy?: string
}

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
}

export function RegisterPage() {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<FormData>({
    name: '',
    surname: '',
    email: '',
    password: '',
    confirmPassword: '',
    privacy: false,
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitted, setSubmitted] = useState(false)

  const validate = (): boolean => {
    const newErrors: FormErrors = {}
    if (!formData.name.trim()) newErrors.name = t('auth.errorRequired')
    if (!formData.surname.trim()) newErrors.surname = t('auth.errorRequired')
    if (!formData.email.trim()) {
      newErrors.email = t('auth.errorRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('auth.errorEmail')
    }
    if (!formData.password.trim()) {
      newErrors.password = t('auth.errorRequired')
    } else if (formData.password.length < 8) {
      newErrors.password = t('auth.errorPasswordMin')
    }
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = t('auth.errorRequired')
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.errorPasswordMatch')
    }
    if (!formData.privacy) newErrors.privacy = t('auth.errorPrivacy')
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      setSubmitted(true)
    }
  }

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  return (
    <>
      <SEOHead titleKey="seo.register.title" descriptionKey="seo.register.description" />

      <section className="min-h-[calc(100vh-80px)] flex items-center justify-center bg-surface-light py-16 px-4">
        <motion.div {...fadeInUp} className="w-full max-w-md">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card p-8 text-center"
            >
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserPlus className="w-8 h-8 text-success" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t('auth.registerSuccess')}</h3>
              <p className="text-text-muted mb-6">{t('auth.registerSuccessDesc')}</p>
              <LocalizedLink to="login" className="btn-primary inline-block">
                {t('auth.signIn')}
              </LocalizedLink>
            </motion.div>
          ) : (
            <div className="card p-8">
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold mb-2">{t('auth.registerTitle')}</h1>
                <p className="text-text-muted text-sm">{t('auth.registerSubtitle')}</p>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      {t('auth.nameLabel')} *
                    </label>
                    <input
                      id="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent ${
                        errors.name ? 'border-accent' : 'border-surface-border'
                      }`}
                      placeholder={t('auth.namePlaceholder')}
                    />
                    {errors.name && <p className="text-accent text-xs mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="surname" className="block text-sm font-medium mb-2">
                      {t('auth.surnameLabel')} *
                    </label>
                    <input
                      id="surname"
                      type="text"
                      required
                      value={formData.surname}
                      onChange={(e) => handleChange('surname', e.target.value)}
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent ${
                        errors.surname ? 'border-accent' : 'border-surface-border'
                      }`}
                      placeholder={t('auth.surnamePlaceholder')}
                    />
                    {errors.surname && <p className="text-accent text-xs mt-1">{errors.surname}</p>}
                  </div>
                </div>

                <div className="mb-5">
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    {t('auth.emailLabel')} *
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={`w-full px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent ${
                      errors.email ? 'border-accent' : 'border-surface-border'
                    }`}
                    placeholder={t('auth.emailPlaceholder')}
                  />
                  {errors.email && <p className="text-accent text-xs mt-1">{errors.email}</p>}
                </div>

                <div className="mb-5">
                  <label htmlFor="password" className="block text-sm font-medium mb-2">
                    {t('auth.passwordLabel')} *
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className={`w-full px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent ${
                      errors.password ? 'border-accent' : 'border-surface-border'
                    }`}
                    placeholder={t('auth.passwordPlaceholder')}
                  />
                  {errors.password && <p className="text-accent text-xs mt-1">{errors.password}</p>}
                </div>

                <div className="mb-5">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                    {t('auth.confirmPasswordLabel')} *
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    className={`w-full px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent ${
                      errors.confirmPassword ? 'border-accent' : 'border-surface-border'
                    }`}
                    placeholder={t('auth.confirmPasswordPlaceholder')}
                  />
                  {errors.confirmPassword && <p className="text-accent text-xs mt-1">{errors.confirmPassword}</p>}
                </div>

                <div className="mb-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      checked={formData.privacy}
                      onChange={(e) => handleChange('privacy', e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-surface-border text-accent focus:ring-accent"
                    />
                    <span className="text-sm text-text-muted">
                      {t('auth.privacyConsent')} *
                    </span>
                  </label>
                  {errors.privacy && <p className="text-accent text-xs mt-1">{errors.privacy}</p>}
                </div>

                <button type="submit" className="btn-primary w-full">
                  {t('auth.signUp')}
                </button>
              </form>

              <p className="text-center text-sm text-text-muted mt-6">
                {t('auth.hasAccount')}{' '}
                <LocalizedLink to="login" className="text-accent hover:underline font-medium">
                  {t('auth.signIn')}
                </LocalizedLink>
              </p>
            </div>
          )}
        </motion.div>
      </section>
    </>
  )
}
